/**
 * The two charts the demo report needs that the sales page did not.
 *
 * `RankBars` and `Funnel` in SalesCharts.jsx already cover a ranked magnitude
 * and a drop-off; what a report adds is a run of periods to read left to
 * right, and a two-way split to read as one line.
 *
 * The rules there hold here:
 *
 *   **Counts over time are one series in one hue.** The height already says
 *   how many, so painting each column by its own value would be the same
 *   number said twice, and colour-by-rank is the thing that repaints the
 *   chart when a filter changes. The selected period is the only column
 *   picked out, because "which one am I reading" is a different question
 *   from "how many".
 *
 *   **A two-way split is two identities**, so it is the one place a second
 *   hue is earned. The pair is validated against both surfaces, and both
 *   segments carry their own number — cyan on white does not clear 3:1, so
 *   the label, not the colour, is what states the value.
 */
import { useState } from 'react';

/* ------------------------------------------------------------ the trend */

/**
 * Demos per day, week, month or year, newest on the right.
 *
 * Columns rather than a line: these are counts of separate periods, not a
 * quantity flowing through time, and a line between two Mondays implies
 * readings in between that nobody took.
 */
export function PeriodBars({ points, unit = 'demos' }) {
  const [at, setAt] = useState(null);
  if (!points.length) return <div className="cempty">Nothing booked in this stretch</div>;

  const peak = Math.max(1, ...points.map(p => p.value));
  /* Round the top to a whole number of columns, so the two gridlines below
     it are whole demos rather than 3.5 of one. */
  const max = Math.ceil(peak / 2) * 2;

  return (
    <div className="pbars" onMouseLeave={() => setAt(null)}>
      <div className="pbwrap">
        {[max, max / 2].map(t => (
          <div className="pbgrid" key={t} style={{ bottom: `${(t / max) * 100}%` }}><span>{t}</span></div>
        ))}

        {points.map((p, i) => (
          <div
            className={`pbcol${p.now ? ' now' : ''}${at === i ? ' on' : ''}`}
            key={p.key}
            onMouseEnter={() => setAt(i)}
            title={`${p.label}: ${p.value} ${unit}`}
          >
            {/* The tallest column says its number without being asked; the
                rest say theirs on hover, so the chart is not a wall of
                digits. */}
            {p.value === peak && peak > 0 ? <b className="pbtop">{p.value}</b> : null}
            <div className="pbtrack">
              <div className="pbfill" style={{ height: `${p.value ? Math.max(3, (p.value / max) * 100) : 0}%` }} />
            </div>
            <span className="pblab">{p.label}</span>
          </div>
        ))}
      </div>

      {at !== null ? (
        <div className="pbtip" style={{ left: `${((at + 0.5) / points.length) * 100}%` }}>
          <b>{points[at].value}</b> {unit}
          <span>{points[at].label}</span>
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------ the split */

/**
 * Two kinds of customer as one bar.
 *
 * A pie of two slices is a bar that has been made harder to read; this is the
 * bar. The 2px gap between the segments is the surface showing through, so
 * the join is visible without a border darkening either colour.
 */
export function SplitBar({ parts, empty = 'Nothing yet' }) {
  const total = parts.reduce((n, p) => n + p.value, 0);
  if (!total) return <div className="cempty">{empty}</div>;

  return (
    <div className="split">
      <div className="sptrack">
        {parts.filter(p => p.value).map(p => (
          <div
            key={p.label}
            className="spseg"
            style={{ width: `${(p.value / total) * 100}%`, background: p.color }}
            title={`${p.label}: ${p.value}`}
          />
        ))}
      </div>
      <ul className="splegend">
        {parts.map(p => (
          <li key={p.label}>
            <i style={{ background: p.color }} />
            <span>{p.label}</span>
            <b>{p.value}</b>
            <em>{total ? Math.round((p.value / total) * 100) : 0}%</em>
          </li>
        ))}
      </ul>
    </div>
  );
}
